<template>
  <div class="search-bar">
    <img :src="searchIcon" alt="Search" class="search-icon" />

    <div v-if="activeQuery" class="query-chip">
      {{ activeQuery }}
      <span class="remove-chip" @click="$emit('clearQuery')">✕</span>
    </div>

    <input
      type="text"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      @keydown.enter="$emit('performSearch')"
      placeholder="Поиск"
      class="search-input"
    />

    <CitySelector
      :city="city"
      class="city-selector"
      @openRegionSelection="() => {
        console.log('🔵 Clicked: openRegionSelection');
        $emit('openRegionSelection');
      }"
    />

    <RegionSelection
      v-if="showRegionSelection"
      class="region-popup"
      @citySelected="setCity"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import CitySelector from "../components/CitySelector.vue";
import RegionSelection from "../components/RegionSelection.vue";

import searchDarkIcon from "../assets/search_dark.png";
import searchLightIcon from "../assets/search_light.png";

defineProps({
  modelValue: String,
  activeQuery: String,
  showRegionSelection: Boolean,
  city: {
    type: String,
    default: 'Москва'
  }
});

defineEmits(["update:modelValue", "clearQuery", "openRegionSelection", "performSearch"]);

const searchIcon = ref(searchDarkIcon);

const updateIcon = () => {
  const theme = document.documentElement.getAttribute("data-theme");
  searchIcon.value = theme === "light" ? searchLightIcon : searchDarkIcon;
};

const observer = new MutationObserver(updateIcon);

onMounted(() => {
  updateIcon();
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"]
  });
});

onBeforeUnmount(() => observer.disconnect());
</script>

<style scoped>

.search-bar {
  height: 16px;
  display: flex;
  align-items: center;
  background: #f8f8f8;
  border-radius: 999px;
  padding: 14px 24px;
  width: 100%;
  max-width: 700px;
  border: 1px solid #e2e2e2;
}

[data-theme="dark"] .search-bar {
  background-color: #2b2b2b;
  border: 1px solid #444;
}

.search-icon {
  width: 16px;
  height: 16px;
  margin-right: 12px;
}

.search-input {
  font-family: "Eastman", sans-serif;
  font-weight: 300;
  font-size: 16px;
  border: none;
  outline: none;
  width: 100%;
  background: transparent;
  color: #000000;
}

[data-theme="dark"] .search-input {
  color: #ffffff;
}

.search-input::placeholder {
  color: #888888;
  opacity: 1;
}

.city-selector {
  background: #bfbfbf;
  color: white;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
  padding: 6px 14px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  margin-right: -10px;
}

[data-theme="dark"] .city-selector {
  background: #505050;
  color: white;
}

.query-chip {
  background: #2173e5;
  color: white;
  font-size: 13px;
  font-family: "Eastman-Medium", sans-serif;
  padding: 6px 12px;
  border-radius: 20px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.remove-chip {
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  line-height: 1;
}


</style><style scoped>

/* ============================================================
   SEARCH BAR
============================================================ */

.search-bar {
  width: 100%;
  max-width: 700px;

  min-height: 46px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  padding:
    0
    10px
    0
    16px;

  background:
    rgba(255, 255, 255, 0.92);

  border:
    1px solid
    rgba(25, 40, 60, 0.08);

  border-radius: 16px;

  box-shadow:
    0
    10px
    28px
    rgba(15, 23, 42, 0.045);

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}


.search-bar:focus-within {
  border-color:
    rgba(40, 115, 225, 0.20);

  box-shadow:
    0
    12px
    30px
    rgba(15, 23, 42, 0.065),
    0
    0
    0
    3px
    rgba(40, 120, 225, 0.055);
}


/* ============================================================
   DARK
============================================================ */

[data-theme="dark"] .search-bar {
  background:
    linear-gradient(
      145deg,
      rgba(34, 36, 42, 0.97),
      rgba(29, 31, 36, 0.97)
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0
    14px
    34px
    rgba(0, 0, 0, 0.18);
}


[data-theme="dark"] .search-bar:focus-within {
  border-color:
    rgba(115, 160, 230, 0.16);

  box-shadow:
    0
    16px
    36px
    rgba(0, 0, 0, 0.23),
    0
    0
    0
    3px
    rgba(90, 145, 225, 0.045);
}


/* ============================================================
   SEARCH ICON
============================================================ */

.search-icon {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  margin-right: 11px;

  object-fit: contain;

  opacity: 0.60;

  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}


.search-bar:focus-within
.search-icon {
  opacity: 0.88;

  transform:
    scale(1.04);
}


/* ============================================================
   INPUT
============================================================ */

.search-input {
  min-width: 0;

  flex: 1;

  height: 38px;

  padding: 0;

  background: transparent;

  border: none;

  outline: none;

  color: #30353c;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 14px;

  font-weight: 400;

  line-height: 1;
}


.search-input::placeholder {
  color: #9b9fa6;

  opacity: 1;
}


[data-theme="dark"] .search-input {
  color: #f0f1f3;
}


[data-theme="dark"]
.search-input::placeholder {
  color: #747981;
}


/* ============================================================
   QUERY CHIP
============================================================ */

.query-chip {
  max-width: 190px;

  min-height: 28px;

  display: inline-flex;
  align-items: center;

  gap: 7px;

  flex-shrink: 0;

  box-sizing: border-box;

  margin-right: 10px;

  padding:
    0
    9px
    0
    11px;

  overflow: hidden;

  color: #3479db;

  background:
    rgba(40, 120, 225, 0.075);

  border:
    1px solid
    rgba(40, 120, 225, 0.11);

  border-radius: 9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1;

  white-space: nowrap;

  text-overflow: ellipsis;
}


[data-theme="dark"] .query-chip {
  color: #9bbef0;

  background:
    rgba(80, 130, 210, 0.10);

  border-color:
    rgba(95, 145, 225, 0.12);
}


/* ============================================================
   REMOVE CHIP
============================================================ */

.remove-chip {
  width: 18px;
  height: 18px;

  flex:
    0
    0
    18px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: currentColor;

  background:
    rgba(50, 105, 190, 0.08);

  border-radius: 6px;

  font-size: 9px;

  font-family:
    sans-serif;

  font-weight: 400;

  line-height: 1;

  cursor: pointer;

  opacity: 0.74;

  transition:
    opacity 0.18s ease,
    background-color 0.18s ease,
    transform 0.18s ease;
}


.remove-chip:hover {
  opacity: 1;

  background:
    rgba(50, 105, 190, 0.14);

  transform:
    scale(1.04);
}


.remove-chip:active {
  transform:
    scale(0.92);
}


/* ============================================================
   CITY
============================================================ */

.city-selector {
  min-height: 32px;

  flex-shrink: 0;

  display: inline-flex;
  align-items: center;

  gap: 4px;

  box-sizing: border-box;

  margin-right: 0;

  padding:
    0
    11px;

  color: #667d9f;

  background:
    #f3f6fa;

  border:
    1px solid
    rgba(55, 95, 155, 0.065);

  border-radius: 10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;

  white-space: nowrap;

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}


.city-selector:hover {
  transform:
    translateY(-1px);

  background:
    #edf2f8;

  border-color:
    rgba(55, 105, 180, 0.10);
}


[data-theme="dark"] .city-selector {
  color: #94b3e2;

  background:
    rgba(80, 130, 210, 0.085);

  border-color:
    rgba(95, 145, 225, 0.10);
}


[data-theme="dark"]
.city-selector:hover {
  background:
    rgba(80, 130, 210, 0.13);

  border-color:
    rgba(105, 155, 235, 0.15);
}


/* ============================================================
   REGION POPUP
============================================================ */

.region-popup {
  z-index: 100;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .search-bar {
    max-width: none;

    min-height: 44px;

    padding:
      0
      8px
      0
      13px;

    border-radius: 14px;
  }


  .search-input {
    font-size: 13px;
  }


  .query-chip {
    max-width: 125px;

    margin-right: 7px;
  }


  .city-selector {
    min-height: 30px;

    padding:
      0
      9px;

    font-size: 8px;
  }


  .search-icon {
    width: 14px;
    height: 14px;

    margin-right: 9px;
  }

}


/* ============================================================
   VERY SMALL
============================================================ */

@media (max-width: 430px) {

  .query-chip {
    max-width: 100px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .search-bar,
  .search-icon,
  .remove-chip,
  .city-selector {
    transition: none;
  }

}

</style>